import { AxiosResponse } from 'axios';
import request from '@core/services/request';

import { IDocumentInfo, IDocumentVerification, IUploadedDocument } from '@core/classes/UploadedDocument';
import { DocAssociation } from '@core/classes/SharedEnums';
import DocumentAssociation from '@core/classes/DocumentAssociation';
import { IAPIResponse } from '@core/classes/Responses/SharedClasses';
import IElasticResponseClass from '@core/classes/Responses/ElasticResponseClass';
import ISimpleResponse from '@core/classes/Responses/SimpleResponse';

// #region Request Interfaces
export interface IDocumentUpsertRequest {
	associations: DocumentAssociation[];
	docInfo: Partial<IDocumentInfo>;
	createdBy: string;
	creatorType: DocAssociation;
	creatorId?: string;
}

export interface IDocumentUpsertRequestSingle extends IDocumentUpsertRequest {
	fileData: File;
}

export interface IDocumentUpsertRequestMultiple extends IDocumentUpsertRequest {
	fileData: File[];
}

export interface IDocumentSearchRequest {
	resultsSize?: number;
	associationRequest?: {
		associations: DocumentAssociation[];
		matchAll: boolean;
	};
	docType?: string[];
	source?: string[];
	fileName?: string;
	fileType?: string;
}

export interface IGetDocumentUploadPortalLinkRequest {
	docType: string;
	allowedFileTypes: string[];
	associations: DocumentAssociation[];
	createdBy: string;
	creatorType: DocAssociation;
	creatorId?: string;
}

export interface IDocumentUploadPortalDecryptQueryResponse {
	expirationDate: Date;
	docTypeDisplay: string;
	error?: string;
}
// #endregion

export const documentsAPI = {
	// #region Add / Edit Documents
	/** The`formData` should contain the same fields as the properties inside of `IDocumentUpsertRequest` */
	upsertDocument: (formData: FormData): Promise<AxiosResponse<IAPIResponse<IUploadedDocument>>> => {
		const obj: IDocumentUpsertRequestSingle = null;
		for (const key in obj) {
			if (!formData.has(key) && key !== 'creatorId') throw new Error(`Cannot process request. Form data is missing key: ${key}`);
		}

		return request.post(`/Documents/Upsert`, formData);
	},
	upsertDocumentFromRequest: (upsertRequest: IDocumentUpsertRequestSingle): Promise<AxiosResponse<IAPIResponse<IUploadedDocument>>> => {
		const docForm = new FormData();

		for (const key in upsertRequest) {
			const requestKey = key as keyof IDocumentUpsertRequestSingle;

			if (key === 'fileData') docForm.append(key, upsertRequest[requestKey] as File);
			else if (upsertRequest[requestKey] != null) docForm.append(key, JSON.stringify(upsertRequest[requestKey]));
		}

		return request.post(`/Documents/Upsert`, docForm);
	},
	upsertMultipleFilesAsPDF: (upsertRequest: IDocumentUpsertRequestMultiple): Promise<AxiosResponse<IAPIResponse<IUploadedDocument>>> => {
		const docForm = new FormData();

		for (let i = 0; i < upsertRequest.fileData.length; i++) docForm.append(`fileData_${i}`, upsertRequest.fileData[i]);

		for (const key in upsertRequest) {
			if (key === 'fileData') continue;

			const requestKey = key as keyof IDocumentUpsertRequestMultiple;
			if (upsertRequest[requestKey] != null) docForm.append(key, JSON.stringify(upsertRequest[requestKey]));
		}

		return request.post(`/Documents/UpsertMultiple`, docForm);
	},
	addDocumentVerification: (id: string, verification: IDocumentVerification): Promise<AxiosResponse<IElasticResponseClass>> => {
		return request.post(`/Documents/AddVerification`, { id, verification });
	},
	editDocumentAssociations: (id: string, associationsToAdd: DocumentAssociation[], associationsToDelete: DocumentAssociation[]): Promise<AxiosResponse<boolean>> => {
		return request.post(`/Documents/EditAssociations`, { id, associationsToAdd, associationsToDelete });
	},
	// #endregion

	// #region Retrieve Documents
	getDocumentById: (id: string): Promise<AxiosResponse<IUploadedDocument>> => {
		return request.get(`/Documents/Get/${id}`);
	},
	getDocumentsByAssociations: (
		associations: DocumentAssociation[],
		matchAll: boolean,
		attachAllAssociations: boolean = false,
	): Promise<AxiosResponse<IUploadedDocument[]>> => {
		return request.post(`/Documents/GetByAssociations`, {
			associations,
			matchAll,
			attachAllAssociations,
		});
	},
	downloadDocument: (id: string, fileName: string): Promise<void> => {
		return request.download(`/Documents/Download/${id}`, fileName);
	},
	searchForDocuments: (searchRequest: IDocumentSearchRequest): Promise<AxiosResponse<IUploadedDocument[]>> => {
		return request.post(`/Documents/Search`, searchRequest);
	},
	// #endregion

	// #region Delete Documents
	deleteDocumentById: (id: string): Promise<AxiosResponse<boolean>> => {
		return request.post(`/Documents/Delete/${id}`);
	},
	deleteAllDocumentsByAssociations: (associations: DocumentAssociation[]): Promise<AxiosResponse<IElasticResponseClass>> => {
		return request.post(`/Documents/DeleteByAssociations`, associations);
	},
	// #endregion

	// #region Document Upload Portal
	getPortalLink: (getLinkRequest: IGetDocumentUploadPortalLinkRequest): Promise<AxiosResponse<string>> => {
		return request.post(`/Documents/Portal/GenerateLink`, getLinkRequest);
	},
	sendCustomerPortalLink: (
		storeCode: string,
		customerId: string,
		sendMethod: 'email' | 'text',
		getLinkRequest: IGetDocumentUploadPortalLinkRequest,
	): Promise<AxiosResponse<ISimpleResponse>> => {
		return request.post(`/Documents/Portal/SendLinkToCustomer?storeCode=${storeCode}&customerId=${customerId}&sendMethod=${sendMethod}`, getLinkRequest);
	},
	sendEmployeePortalLink: (
		storeCode: string,
		sendMethod: 'email' | 'text',
		getLinkRequest: IGetDocumentUploadPortalLinkRequest,
	): Promise<AxiosResponse<ISimpleResponse>> => {
		return request.post(`/Documents/Portal/SendLinkToEmployee?storeCode=${storeCode}&sendMethod=${sendMethod}`, getLinkRequest);
	},
	decryptPortalQuery: (encryptedExpiration: string, docTypeCode: string): Promise<AxiosResponse<IDocumentUploadPortalDecryptQueryResponse>> => {
		const encodedExpiration = encodeURIComponent(encryptedExpiration);
		return request.get(`/Documents/Portal/DecryptQuery?encryptedExpiration=${encodedExpiration}&docTypeCode=${docTypeCode}`);
	},
	// #endregion
};
